import {baseUri} from '@jetbrains/teamcity-ui'
import type {KeyValue} from '@jetbrains/teamcity-ui/dist/utils'
import {objectEntries} from '@jetbrains/teamcity-ui/dist/utils'
import {createSelector} from 'reselect'

import type {ActionCreator} from '../actions/types'
import {projectsSidebarApi} from '../components/App/SidebarPanel/SidebarPanelContent/ProjectsSidebar/ProjectsSidebar.api'
import {openEditDialog} from '../components/common/EditProjectSidebar/EditProjectSidebar'
import type {State, TogglingOverview} from '../reducers/types'
import {archivedProjectsArg, sidebarArg} from '../rest/projects'
import {getFavoriteBuildsHref, getFavoriteProjectsHref} from '../routes'
import {restApi} from '../services/rest'
import type {
  BuildTypeId,
  CustomSidebarItemId,
  ExpandState,
  FederationServerData,
  FederationServerId,
  PipelineId,
  ProjectId,
  SidebarProject,
  TitleId,
} from '../types'
import {
  ALL_PROJECTS_TITLE_ID,
  FAVORITES_TITLE_ID,
  ROOT_PROJECT_ID,
  stringifyId,
  toCustomSidebarItemId,
} from '../types'
import {internalProps} from '../types/BS_types'
import type {ProjectsTreeItem, SidebarActiveItem} from '../types/projectTrees'
import {currentServerId} from '../types/projectTrees'
import {convertDataToTree, upgradeTreeByExpandState} from '../utils/convertDataToTree'
import {emptyArray, emptyMap, getEmptyHash} from '../utils/empty'
import type {WritableMap} from '../utils/object'

import {
  getExtensionEndpoint,
  getNoMatchingWords,
  getOverviewData,
  getOverviewProjectsHash,
  getProject,
  getSidebarProjectsReady,
  getOverviewResult,
  getSidebarProjectsHash,
  getSidebarProjectIds,
} from '.'

export type SidebarItemId = string
export type ProjectsTree = {
  data: Array<ProjectsTreeItem>
  _hash?: WritableMap<SidebarItemId, ProjectsTreeItem>
  hash?: WritableMap<SidebarItemId, ProjectsTreeItem>
}
export const generateSidebarItemId = (
  server: FederationServerId,
  type: 'project' | 'pipeline' | 'buildType' | 'template' | 'title' | 'server' | 'link' | 'action',
  id?: ProjectId | BuildTypeId | TitleId | CustomSidebarItemId | FederationServerId,
): SidebarItemId => `${stringifyId(server)}_${type}_${stringifyId(id)}`
export const getOverviewExpandState = (state: State): KeyValue<ProjectId, ExpandState> => {
  const {endpoint} = getExtensionEndpoint(state, 'expandState') ?? {}
  return (
    (endpoint != null && restApi.endpoints.getExpandState.select(endpoint)(state).data) ||
    getEmptyHash()
  )
}
export const getAllProjectsExpandState = (state: State): KeyValue<ProjectId, ExpandState> =>
  state.allProjectsExpandState

export const getSearchProjectsExpandState: (
  arg0: State,
) => KeyValue<ProjectId, ExpandState> | null | undefined = state => state.searchProjectsExpandState

export const getOverviewReady: (state: State) => boolean = state =>
  getOverviewResult(state).data != null
export const getHasFavoriteProjects: (arg0: State) => boolean | null | undefined = state =>
  getOverviewReady(state) ? getOverviewData(state).length > 0 : null

export const getShowArchivedProjects = (state: State): boolean =>
  state.projectsSidebar.showArchivedProjects
const getCurrentSidebarArg = (state: State, showArchived?: boolean) =>
  showArchived && getSidebarProjectsReady(state, archivedProjectsArg)
    ? archivedProjectsArg
    : sidebarArg

const getProjectsData = (state: State, showArchived?: boolean) =>
  getSidebarProjectIds(state, getCurrentSidebarArg(state, showArchived))
export const getCurrentSidebarProjectsHash = (state: State, showArchived?: boolean) =>
  getSidebarProjectsHash(state, getCurrentSidebarArg(state, showArchived))

const DEFAULT_BUILDTYPES_LIMIT = 200
const buildTypesLimit =
  internalProps['teamcity.overviewPage.buildTypes.limit'] ?? DEFAULT_BUILDTYPES_LIMIT
export const getCanShowAllProjects = (state: State): boolean => {
  const buildTypesCount = projectsSidebarApi.endpoints.getBuildTypesCount.select()(state).data
  return buildTypesCount != null && buildTypesCount <= buildTypesLimit
}

export const getProjectsAndOverviewReady = (state: State) =>
  getOverviewReady(state) && getSidebarProjectsReady(state)
export const getFederationServersData: (
  arg0: State,
) => KeyValue<FederationServerId, FederationServerData> = state => state.federationServersData

export const favoritesTitleItem: ProjectsTreeItem = {
  itemType: 'title',
  id: FAVORITES_TITLE_ID,
  name: 'FAVORITES',
  level: 0,
  group: 'favorites',
  serverId: currentServerId,
  editable: true,
  focusable: false,
}

const getLinkAsTreeItem: (arg0: {
  href: string
  name: string
  level?: number
  serverId?: FederationServerId
  icon?: string | null | undefined
}) => ProjectsTreeItem = ({href, name, level = 0, serverId = currentServerId, icon}) => ({
  itemType: 'link',
  id: toCustomSidebarItemId(href),
  href,
  icon,
  name,
  level,
  group: 'favorites',
  serverId,
  focusable: true,
})

export const favoriteBuildsLinkItem: ProjectsTreeItem = getLinkAsTreeItem({
  name: 'Favorite builds',
  href: getFavoriteBuildsHref(),
  icon: 'star-empty',
})

export const favoriteProjectsLinkItem = getLinkAsTreeItem({
  name: 'Overview',
  href: getFavoriteProjectsHref(),
  icon: 'text',
})

export const actions: Record<string, ActionCreator> = {
  addToFavorites: () => openEditDialog(),
}

export const createAllProjectsTitleItem = (collapsed = true, isEdit = false): ProjectsTreeItem => ({
  itemType: 'title',
  id: ALL_PROJECTS_TITLE_ID,
  name: isEdit ? 'EVERYTHING' : 'PROJECTS',
  level: 0,
  group: 'all',
  expanded: !collapsed,
  hasChildren: !isEdit,
  serverId: currentServerId,
  focusable: true,
})
export const getAllProjectsTitleItem: (arg0: State) => ProjectsTreeItem = createSelector(
  (state: State) => state.projectsSidebar.allProjectsCollapsed,
  collapsed => createAllProjectsTitleItem(collapsed, false),
)

const getServerTitleItem = (
  id: FederationServerId,
  authorized = true,
  loading = false,
): ProjectsTreeItem => ({
  id,
  loading,
  authorized,
  level: 0,
  hasChildren: true,
  itemType: 'server',
  name: stringifyId(id),
  expanded: true,
  serverId: id,
  group: 'search',
})

export type TreeFilter = {
  parentId?: ProjectId
  removePipelines?: boolean
  removeBuildTypes?: boolean
  removeEmptyProjects?: boolean
  removeSiblingBuildTypes?: boolean
  includedBuildTypes?: ReadonlyArray<BuildTypeId> | null | undefined
  excludedBuildTypes?: ReadonlyArray<BuildTypeId> | null | undefined
  includedPipelines?: ReadonlyArray<PipelineId> | null | undefined
  excludedPipelines?: ReadonlyArray<PipelineId> | null | undefined
  includedProjects?: ReadonlyArray<ProjectId> | null | undefined
  includedRootProjects?: ReadonlyArray<ProjectId> | null | undefined
  disabledProjects?: ReadonlyArray<ProjectId> | null | undefined
  includeRoot?: boolean
}

export const createDefaultOverviewTree = (
  ready: boolean,
  data: readonly ProjectId[],
  projects: KeyValue<ProjectId, SidebarProject>,
) =>
  convertDataToTree({
    ready,
    data,
    projects,
    group: 'favorites',
    onlyIncluded: true,
  })

export const getDefaultOverviewTree: (state: State) => ProjectsTree | null | undefined =
  createSelector(
    (state: State) => getOverviewReady(state),
    getOverviewData,
    getOverviewProjectsHash,
    createDefaultOverviewTree,
  )
const getOverviewTree: (arg0: State) => ProjectsTree = createSelector(
  getDefaultOverviewTree,
  getOverviewExpandState,
  (defaultTree, expandState) =>
    upgradeTreeByExpandState({
      defaultTree,
      expandState,
      baseLevel: 0,
    }),
)
export const getOverviewTreeData: (arg0: State) => ReadonlyArray<ProjectsTreeItem> = createSelector(
  getOverviewTree,
  tree => tree.data,
)
export const getOverviewTreeHash: (state: State) => WritableMap<SidebarItemId, ProjectsTreeItem> =
  createSelector(getOverviewTree, tree => tree?.hash || emptyMap)

const prepareAllProjectAndBuildTypesToConvert: (
  state: State,
  showArchived?: boolean,
) => {
  ready: boolean
  data: any
  projects: any
} = createSelector(
  (state: State) => getSidebarProjectsReady(state),
  getProjectsData,
  getCurrentSidebarProjectsHash,
  (ready, data, projects) => ({
    ready,
    data,
    projects,
  }),
)

export const getDefaultAllProjectsTree: (state: State) => ProjectsTree | null | undefined =
  createSelector(prepareAllProjectAndBuildTypesToConvert, ({ready, data, projects}) =>
    convertDataToTree({
      ready,
      data,
      projects,
      group: 'all',
    }),
  )

export const getDefaultAllProjectsSidebarTree: (state: State) => ProjectsTree | null | undefined =
  createSelector(
    state => prepareAllProjectAndBuildTypesToConvert(state, getShowArchivedProjects(state)),
    getShowArchivedProjects,
    (state: State) => state.projectsSidebar.allProjectsCollapsed,
    ({ready, data, projects}, showArchivedProjects, allProjectsCollapsed) =>
      convertDataToTree({
        ready: ready && !allProjectsCollapsed,
        data,
        projects,
        group: 'all',
        showArchivedProjects,
      }),
  )

export const getAllAncestorProjectsReady = (state: State) => {
  const projectId = state.projectsSidebar.activeItem?.activeProjectId
  const isFavoriteItemOpen = state.projectsSidebar.activeItem?.isFavoriteItemOpen
  const ancestorProjects = getProject(state, projectId)?.ancestorProjects?.project
  if (!projectId || isFavoriteItemOpen) {
    return true
  }
  if (!ancestorProjects) {
    return false
  }
  return (
    ancestorProjects.every(id => id === ROOT_PROJECT_ID || getProject(state, id) != null) ||
    getSidebarProjectsReady(state)
  )
}

const getAllProjectsTree: (state: State) => ProjectsTree = createSelector(
  getDefaultAllProjectsSidebarTree,
  getAllProjectsExpandState,
  getShowArchivedProjects,
  (defaultTree, expandState, showArchivedProjects) =>
    upgradeTreeByExpandState({
      defaultTree,
      expandState,
      baseLevel: 1,
      showArchivedProjects,
    }),
)
export const getAllProjectsTreeData: (
  arg0: State,
) => ReadonlyArray<ProjectsTreeItem> | null | undefined = createSelector(
  getAllProjectsTree,
  tree => tree?.data,
)
export const getAllProjectsTreeHash: (
  state: State,
) => WritableMap<SidebarItemId, ProjectsTreeItem> = createSelector(
  getAllProjectsTree,
  tree => tree?.hash || emptyMap,
)
export const getMergedProjectsData = () =>
  createSelector(getProjectsData, getOverviewData, (data, overviewData) => [
    ...new Set([...overviewData, ...data]),
  ])
export function getIsStarred({
  item,
  togglingOverview,
  overviewData,
  overviewProjectsHash,
}: {
  item: ProjectsTreeItem
  togglingOverview: TogglingOverview
  overviewData: Set<ProjectId>
  overviewProjectsHash: KeyValue<ProjectId, SidebarProject>
}): boolean | null | undefined {
  if (item.isStarred != null) {
    return item.isStarred
  }

  if (item.serverId !== currentServerId) {
    return null
  }

  if (item.itemType === 'project') {
    const toggling = togglingOverview.project[item.id]
    return toggling ?? overviewData.has(item.id)
  } else if (item.itemType === 'buildType') {
    const toggling = togglingOverview.bt[item.id!]

    if (toggling != null) {
      return toggling
    } else if (overviewData.has(item.parentId)) {
      const buildTypes = overviewProjectsHash[item.parentId]?.buildTypes?.buildType
      return buildTypes ? buildTypes.some(({id}) => id === item.id) : false
    } else {
      return false
    }
  }

  return null
}
type CreateSearchProjectsTreeParams = {
  searchQuery: string
  defaultTreeByServer: WritableMap<FederationServerId, ProjectsTree | null | undefined>
  togglingOverview: TogglingOverview
  overviewData: ReadonlyArray<ProjectId>
  overviewProjectsHash: KeyValue<ProjectId, SidebarProject>
  federationServers?: ReadonlyArray<FederationServerId>
  parentId?: ProjectId
  includeRoot?: boolean
  strictSearchMode?: boolean
}
export function createSearchProjectsTree({
  searchQuery,
  defaultTreeByServer,
  togglingOverview,
  overviewData,
  overviewProjectsHash,
  federationServers = emptyArray,
  parentId = ROOT_PROJECT_ID,
  includeRoot = false,
  strictSearchMode = false,
}: CreateSearchProjectsTreeParams) {
  const servers = [currentServerId, ...federationServers]
  const result: WritableMap<FederationServerId, ProjectsTree> = new Map()
  const overviewDataSet = new Set(overviewData)
  const words: Array<string> = searchQuery.toLowerCase().split(' ').filter(Boolean)

  servers.forEach((serverId: FederationServerId) => {
    const defaultTree = defaultTreeByServer.get(serverId)
    let data: ProjectsTreeItem[] = []

    if (defaultTree?.data?.length) {
      const defaultTreeData = defaultTree.data
      const defaultTreeLength = defaultTreeData.length
      let index = 0

      if (words.length) {
        const wordStateMap = new Map()

        while (index < defaultTreeLength) {
          const treeItem = defaultTreeData[index]
          const {
            id: treeItemId,
            name: treeItemName,
            parentId: treeItemParentId,
            itemType: treeItemType,
          } = treeItem
          const possibleRootId = includeRoot ? treeItemId : treeItemParentId

          if (treeItemType === 'server') {
            data.push(treeItem)

            index++

            continue
          }

          if (possibleRootId == null || (possibleRootId !== parentId && treeItemParentId == null)) {
            index++
            continue
          }

          const nextIndex = index + 1
          const isItemTypeProject = treeItemType === 'project'
          const {level: nextTreeItemLevel} = defaultTreeData[nextIndex] ?? {}
          const parentUnmatchedWords = wordStateMap.get(treeItemParentId) || words
          const remainingWords = getNoMatchingWords(
            treeItemName.toLowerCase(),
            parentUnmatchedWords,
          )
          treeItem.isFullMatch =
            !remainingWords.length &&
            (parentUnmatchedWords.length > 0 ||
              // if it's not a strict search, include an item if its parent fully matches the searchQuery
              (!strictSearchMode && parentUnmatchedWords.length === 0) ||
              words.some(word => treeItemName.toLowerCase().includes(word)))
          treeItem.hasChildren = nextTreeItemLevel != null && nextTreeItemLevel > treeItem.level

          data.push(treeItem)

          if (isItemTypeProject) {
            wordStateMap.set(treeItemId, remainingWords)
          }

          let reverseIndex = data.length - 1

          while (
            data[reverseIndex] &&
            (data[reverseIndex]?.level >= nextTreeItemLevel || !nextTreeItemLevel)
          ) {
            if (data[reverseIndex]?.isFullMatch) {
              // mark matched project with no child matches as non-expandable
              data[reverseIndex].hasChildren = false
              break
            }
            data.pop()
            reverseIndex--
          }

          index++
        }
        data.forEach(treeItem => {
          treeItem.isStarred = getIsStarred({
            item: treeItem,
            togglingOverview,
            overviewData: overviewDataSet,
            overviewProjectsHash,
          })
        })
      } else {
        data = [...defaultTree?.data]
      }
    }

    result.set(serverId, {
      data,
    })
  })

  return result
}

export const upgradeSearchProjectsTreeByExpandState = (
  federationServers: ReadonlyArray<FederationServerId>,
  searchProjectsTree: WritableMap<FederationServerId, ProjectsTree>,
  expandStates: KeyValue<ProjectId, ExpandState> | null | undefined,
  defaultExpandState = true,
) => {
  const expandStatesEntries = expandStates != null ? objectEntries(expandStates) : []
  const expandStateMap = new Map(expandStatesEntries)
  const expandStateMapSize = expandStateMap.size
  const servers = [currentServerId, ...federationServers]

  if (expandStateMapSize || !defaultExpandState) {
    const result: WritableMap<FederationServerId, ProjectsTree> = new Map()

    servers.forEach(server => {
      const projectsTree = searchProjectsTree.get(server)
      const data = []
      let index = 0
      let expandParentLevel = Infinity

      if (projectsTree?.data?.length) {
        while (index < projectsTree?.data.length) {
          let treeItem = projectsTree.data[index]
          const {itemType, level, id} = treeItem

          index++

          if (level > expandParentLevel) {
            continue
          } else {
            expandParentLevel = Infinity
          }

          if (treeItem && itemType === 'project') {
            const expandState = expandStateMap.get(id)?.expandState
            const isExpanded = expandState ? expandState === 'EXPANDED' : defaultExpandState

            treeItem = {...treeItem, expanded: isExpanded}

            if (!isExpanded && expandParentLevel === Infinity) {
              expandParentLevel = level
            }
          }

          data.push(treeItem)
        }
      }

      result.set(server, {
        data,
        _hash: undefined,
        get hash() {
          if (!this._hash) {
            this._hash = new Map(this.data.map(item => [item.key!, item]))
          }

          return this._hash
        },
      })
    })

    return result
  }

  return searchProjectsTree
}

export const mergeSearchProjectsTree = (
  treeByServer: WritableMap<FederationServerId, ProjectsTree>,
  federationServers: ReadonlyArray<FederationServerId>,
  federationServersData: KeyValue<FederationServerId, FederationServerData>,
): ProjectsTree => {
  let treeData: Array<ProjectsTreeItem> = []
  const servers = [currentServerId, ...federationServers]

  servers.forEach((serverId: FederationServerId) => {
    const tree = treeByServer.get(serverId)
    const serverData = federationServersData[serverId]

    if (tree?.data && (tree.data.length || (serverId !== baseUri && !serverData?.authorized))) {
      if (federationServers?.length) {
        if (serverId === baseUri) {
          treeData.push(getServerTitleItem(currentServerId))
        } else {
          const serverTitle = getServerTitleItem(
            serverId,
            Boolean(serverData?.authorized),
            serverData?.projects == null,
          )
          treeData.push(serverTitle)
        }
      }

      if (serverId === baseUri || Boolean(serverData?.authorized)) {
        treeData = treeData.concat(tree.data)
      }
    }
  })

  return {
    data: treeData,
  }
}
export function mergeProjectsForSidebar(
  favoriteProjects: ReadonlyArray<ProjectsTreeItem>,
  hasFavoriteProjects: boolean | null | undefined,
  allProjects: ReadonlyArray<ProjectsTreeItem> | null | undefined,
  allProjectsTitleItem: ProjectsTreeItem,
  needsFavourites: boolean,
  isFavoriteItemOpen?: boolean,
): ReadonlyArray<ProjectsTreeItem> {
  let items: Array<ProjectsTreeItem> = []

  if (needsFavourites) {
    items.push(favoriteBuildsLinkItem)
  }
  if (hasFavoriteProjects === true) {
    items.push(favoritesTitleItem)
    items.push(favoriteProjectsLinkItem)
    items = items.concat(favoriteProjects)
  }

  items.push(allProjectsTitleItem)

  if (
    allProjects?.length &&
    ((allProjectsTitleItem.expanded === false && !isFavoriteItemOpen) ||
      allProjectsTitleItem.expanded === true)
  ) {
    items = items.concat(allProjects)
  }

  return items.length ? items : emptyArray
}

export const matchSidebarItem =
  (activeItem: SidebarActiveItem | null | undefined) =>
  ({group: nodeGroup, itemType, id: nodeId}: ProjectsTreeItem): boolean => {
    if (activeItem == null) {
      return false
    }
    const {nodeType, id: activeItemId, group: activeItemGroup} = activeItem

    if (nodeType === 'bt' || nodeType === 'project' || nodeType === 'pipeline') {
      if (nodeGroup !== activeItemGroup && nodeGroup !== 'all' && nodeGroup !== 'search') {
        return false
      }
    }

    switch (itemType) {
      case 'project':
        return nodeType === 'project' && activeItemId === nodeId

      case 'buildType':
        return nodeType === 'bt' && activeItemId === nodeId

      case 'pipeline':
        return nodeType === 'pipeline' && activeItemId === nodeId

      case 'link':
        return nodeType === 'link' && activeItemId === nodeId

      case 'action':
        return nodeType === 'all' && nodeId === toCustomSidebarItemId('all item')

      default:
        return false
    }
  }
